import axios, { AxiosError } from "axios";
import type { ApiErrorPayload } from "./types";
import { getAccessToken } from "./session-token";

export const api = axios.create({
  baseURL:
    import.meta.env.VITE_API_URL ??
    (import.meta.env.DEV ? "http://127.0.0.1:8000" : ""),
  timeout: 15_000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as (typeof error.config & { _retried?: boolean }) | undefined;
    const url = config?.url ?? "";
    if (
      error.response?.status === 401 &&
      config &&
      !config._retried &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/refresh")
    ) {
      config._retried = true;
      const { useAuthStore } = await import("../stores/auth");
      if (await useAuthStore().refresh()) return api.request(config);
    }
    return Promise.reject(error);
  },
);

export function apiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    if (error.code === "ECONNABORTED" || error.message?.toLowerCase().includes("timeout")) {
      return "La solicitud tardó demasiado tiempo en responder (tiempo de espera agotado).";
    }
    if (!error.response) {
      return "No fue posible conectar con la API. Verifica que el backend esté activo.";
    }
    const data = error.response.data as any;
    if (typeof data === "string") return data;
    if (data?.detail) {
      return typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
    }
    if (data?.message) return String(data.message);
    if (data?.error?.message) return String(data.error.message);
    return "No fue posible completar la operación.";
  }
  return "Ocurrió un error inesperado.";
}
